/**
 * Built-in workflow handlers for DataList lifecycle events.
 *
 * Provides handler functions for action-complete, bulk-action-complete,
 * and fetch-error workflows that are driven by the DataListWorkflowConfig.
 * Custom handlers registered in the workflow registry are invoked when
 * configured.
 *
 * @see plan section 2.5
 */

import type { DataListWorkflowConfig } from '../types';
import { dataListWorkflowRegistry } from './registry';

// ---------------------------------------------------------------------------
// handleActionComplete
// ---------------------------------------------------------------------------

/**
 * Handles completion of a row action.
 *
 * - Calls refreshFn if `config.onActionComplete.refresh !== false` (default: true)
 * - Logs a notification if configured (placeholder for toast integration)
 * - Calls a custom handler from the registry if configured
 *
 * When config is undefined, refreshFn is called as the safe default.
 *
 * @param config           - The workflow configuration, or undefined.
 * @param refreshFn        - Function to refresh the list data.
 * @param _clearSelectionFn - Function to clear row selection (unused for row actions).
 */
export function handleActionComplete(
  config: DataListWorkflowConfig | undefined,
  refreshFn: () => void,
  _clearSelectionFn: () => void,
): void {
  // No config: apply safe defaults (refresh)
  if (!config) {
    refreshFn();
    return;
  }

  const onActionComplete = config.onActionComplete;

  // Refresh (default: true)
  if (onActionComplete?.refresh !== false) {
    refreshFn();
  }

  // Notification (placeholder for real toast integration)
  if (onActionComplete?.notification) {
    // eslint-disable-next-line no-console
    console.log(
      `[DataList] notification: ${onActionComplete.notification.messageKey}`,
      onActionComplete.notification.variant ?? 'success',
    );
  }

  // Custom handler from registry
  if (onActionComplete?.custom) {
    const handler = dataListWorkflowRegistry.getCustomHandler(
      onActionComplete.custom,
    );
    if (handler) {
      handler();
    }
  }
}

// ---------------------------------------------------------------------------
// handleBulkActionComplete
// ---------------------------------------------------------------------------

/**
 * Handles completion of a bulk action.
 *
 * - Calls refreshFn if `config.onBulkActionComplete.refresh !== false` (default: true)
 * - Calls clearSelectionFn if `config.onBulkActionComplete.clearSelection !== false` (default: true)
 * - Logs a notification if configured (placeholder for toast integration)
 * - Calls a custom handler from the registry if configured
 *
 * When config is undefined, both refreshFn and clearSelectionFn are called
 * as the safe default.
 *
 * @param config           - The workflow configuration, or undefined.
 * @param refreshFn        - Function to refresh the list data.
 * @param clearSelectionFn - Function to clear row selection.
 */
export function handleBulkActionComplete(
  config: DataListWorkflowConfig | undefined,
  refreshFn: () => void,
  clearSelectionFn: () => void,
): void {
  // No config: apply safe defaults (refresh + clear selection)
  if (!config) {
    refreshFn();
    clearSelectionFn();
    return;
  }

  const onBulkActionComplete = config.onBulkActionComplete;

  // Refresh (default: true)
  if (onBulkActionComplete?.refresh !== false) {
    refreshFn();
  }

  // Clear selection (default: true)
  if (onBulkActionComplete?.clearSelection !== false) {
    clearSelectionFn();
  }

  // Notification (placeholder for real toast integration)
  if (onBulkActionComplete?.notification) {
    // eslint-disable-next-line no-console
    console.log(
      `[DataList] notification: ${onBulkActionComplete.notification.messageKey}`,
      onBulkActionComplete.notification.variant ?? 'success',
    );
  }

  // Custom handler from registry
  if (onBulkActionComplete?.custom) {
    const handler = dataListWorkflowRegistry.getCustomHandler(
      onBulkActionComplete.custom,
    );
    if (handler) {
      handler();
    }
  }
}

// ---------------------------------------------------------------------------
// handleFetchError
// ---------------------------------------------------------------------------

/**
 * Handles a data fetch error.
 *
 * - Logs a notification if `config.onError.notification` is configured
 *   (placeholder for toast integration)
 * - Calls a custom handler from the registry if configured
 *
 * When config is undefined, returns without side effects.
 *
 * @param config - The workflow configuration, or undefined.
 * @param error  - The error from the failed fetch.
 */
export function handleFetchError(
  config: DataListWorkflowConfig | undefined,
  error: unknown,
): void {
  // No config: nothing to do
  if (!config) {
    return;
  }

  const onError = config.onError;

  // Notification (placeholder for real toast integration)
  if (onError?.notification) {
    // eslint-disable-next-line no-console
    console.log(
      `[DataList] notification: ${onError.notification.messageKey}`,
      onError.notification.variant ?? 'error',
    );
  }

  // Custom handler from registry
  if (onError?.custom) {
    const handler = dataListWorkflowRegistry.getCustomHandler(onError.custom);
    if (handler) {
      handler(error);
    }
  }
}
